import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import type { PropertyGroupResponse } from '@/shared/api';
import { NameConfirmation, namesMatch } from '@/features/properties/lifecycle/NameConfirmation';

interface Props {
  group: PropertyGroupResponse;
  pending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Archivieren ist der einzige Weg, ein Objekt loszuwerden. Deshalb dieselbe Reibung wie beim
 * Apartment: erst den Namen tippen, dann der Knopf — plus der ausdrückliche Hinweis, was bleibt.
 */
export function ArchiveObjectDialog({ group, pending, onClose, onConfirm }: Props) {
  const [typed, setTyped] = useState('');
  const confirmed = namesMatch(group.name, typed);

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>„{group.name}" archivieren</DialogTitle>
          <DialogDescription>
            Das Objekt verschwindet aus allen Listen und lässt sich nicht über die Oberfläche
            zurückholen. Adresse, Anfahrhilfe und Parkplätze des Objekts sind danach nicht mehr
            erreichbar.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <p
            className="border-sealwax/40 bg-sealwax/10 text-ink rounded-md border p-3 text-sm"
            role="alert"
          >
            Diese Aktion ist endgültig. Sie betrifft das Objekt selbst — die Buchungen und Belege
            der einst zugehörigen Apartments bleiben bestehen.
          </p>
          <NameConfirmation
            expectedName={group.name}
            value={typed}
            disabled={pending}
            subjectLabel="Objekts"
            onChange={setTyped}
          />
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            loading={pending}
            disabled={pending || !confirmed}
            onClick={onConfirm}
          >
            Endgültig archivieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
